import {
  ExternalRegionMode,
  type ListResult,
  type Region,
  type RegionFilter,
  type RegionSortableField,
  type ScaleOperation,
  type ScaleStatus,
} from 'common/api/v1/types'
import { EdgeClient } from '../../../../common/src/generated/edgeClient'
import type { RequestOptions } from 'common/network/request-options'
import { PaginatedRequestParams, singleSortQueryFromPaginatedRequestParams, type EnrichedRegion } from '../nm-types'

type ScaleInfo = Pick<ScaleStatus, 'replicas' | 'currentReplicas'>

export type RegionDetails = {
  region: EnrichedRegion
  videoScale?: ScaleInfo
  thumbScale?: ScaleInfo
  edgeConnectScale?: ScaleInfo
}

export class RegionApi {
  constructor(private readonly edgeClient: EdgeClient) {}

  #externalToText(external: Region['external']): EnrichedRegion['_externalText'] {
    switch (external) {
      case ExternalRegionMode.core:
        return 'core'
      case ExternalRegionMode.externalK8s:
        return 'external (kubernetes)'
      case ExternalRegionMode.external:
        return 'external'
      default:
        return 'N/A'
    }
  }

  async getRegions(
    { filter: searchName, ...params }: PaginatedRequestParams<RegionSortableField>,
    onlyVideoRegions: boolean = false,
    options?: Pick<RequestOptions, 'signal'>,
  ): Promise<ListResult<Region>> {
    const filter: RegionFilter = { searchName, onlyVideoRegions }
    const query = singleSortQueryFromPaginatedRequestParams({ filter, paginatedRequestParams: params })
    const res = await this.edgeClient.listRegions(query, options)
    return {
      ...res,
      items: res.items.map((r) => ({
        ...r,
        _externalText: this.#externalToText(r.external),
      })),
    }
  }

  async getRegion(id: Region['id']): Promise<RegionDetails['region']> {
    const region = await this.edgeClient.getRegion(id)
    const appliancesTotal = await this.edgeClient
      .listAppliances({ filter: { regionNames: [region.name] } })
      .then((res) => res.total)
    return {
      ...region,
      _externalText: this.#externalToText(region.external),
      appliancesTotal,
    }
  }

  /**
   * Fetches scale information for K8s-managed regions (core, externalK8s).
   * Returns undefined for external regions as they don't have K8s-managed scaling.
   */
  async getRegionScale(
    id: Region['id'],
    external: ExternalRegionMode,
  ): Promise<Pick<RegionDetails, 'videoScale' | 'thumbScale' | 'edgeConnectScale'>> {
    if (external === ExternalRegionMode.external) {
      return {}
    }

    const [videoScale, thumbScale] = await Promise.all([
      this.edgeClient.getRegionVideoScale(id).then((res) => ({
        replicas: res.replicas ?? res.currentReplicas,
        currentReplicas: res.currentReplicas,
      })),
      this.edgeClient.getRegionThumbScale(id).then((res) => ({
        replicas: res.replicas ?? res.currentReplicas,
        currentReplicas: res.currentReplicas,
      })),
    ])

    // edge-connect runs only in the core region; tolerate a missing statefulset on older clusters.
    const edgeConnectScale =
      external === ExternalRegionMode.core
        ? await this.edgeClient
            .getRegionEdgeConnectScale(id)
            .then((res) => ({
              replicas: res.replicas ?? res.currentReplicas,
              currentReplicas: res.currentReplicas,
            }))
            .catch(() => undefined)
        : undefined

    return { videoScale, thumbScale, edgeConnectScale }
  }

  setVideoScale(regionId: string, count: number): Promise<ScaleOperation> {
    return this.edgeClient.setRegionVideoScale(regionId, { count, waitSeconds: 1 })
  }
  setThumbScale(regionId: string, count: number): Promise<ScaleOperation> {
    return this.edgeClient.setRegionThumbScale(regionId, { count, waitSeconds: 1 })
  }
  setEdgeConnectScale(regionId: string, count: number): Promise<ScaleOperation> {
    return this.edgeClient.setRegionEdgeConnectScale(regionId, { count, waitSeconds: 1 })
  }

  async setDefaultRegion(regionId: Region['id']) {
    return this.edgeClient.setRegionDefaultRegion({ id: regionId })
  }

  async deleteRegion(regionId: Region['id']): Promise<void> {
    await this.edgeClient.deleteRegion(regionId)
  }

  async createRegion(name: string): Promise<Region> {
    return this.edgeClient.createRegion({ name, external: ExternalRegionMode.external })
  }
}
